/**
 * ValidationNextSteps — the end-of-flow "talk to a Batemo expert" bar shown once
 * a shortlist exists. It turns Ion's prose offer (system prompt v5) into real
 * actions: the customer can request a simulative or measurement-based validation
 * of the chosen cell(s), or leave their profile / tell us what's still missing.
 * Each click is captured server-side (next-step-interest) as both a lead and
 * funnel telemetry, so we can improve the flow over time.
 *
 * Gated on salesModeActive + stage ∈ {shortlist, trade_offs} + an active
 * conversation — same surface as CanvasHandoffCTAs.
 */

import { useState } from 'preact/hooks'
import {
  salesModeActive,
  selectionContext,
  currentConversationId,
} from '../../../state/chatStore.js'
import { recordNextStepInterest } from '../../../services/selectionFlowService.js'
import { t } from '../../../i18n/index.js'

const VALIDATION_CTAS = [
  { cta: 'simulative', key: 'ion.nextstep.simulative' },
  { cta: 'measurement_performance', key: 'ion.nextstep.measurement_performance' },
  { cta: 'measurement_lifetime', key: 'ion.nextstep.measurement_lifetime' },
]

export function ValidationNextSteps() {
  if (!salesModeActive.value) return null
  const stage = selectionContext.value.stage
  if (stage !== 'shortlist' && stage !== 'trade_offs') return null
  const publicId = currentConversationId.value
  if (!publicId) return null

  const [requested, setRequested] = useState(() => new Set())
  const [sending, setSending] = useState(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState(null)

  async function fire(cta, msg) {
    setSending(cta)
    setError(null)
    try {
      await recordNextStepInterest(publicId, cta, msg)
      setRequested((prev) => new Set(prev).add(cta))
      if (cta === 'leave_profile') setMessage('')
    } catch (err) {
      setError(err.message || 'Could not record your request.')
    } finally {
      setSending(null)
    }
  }

  return (
    <section class="ion-nextsteps" aria-label={t('ion.nextstep.title')}>
      <h4 class="ion-nextsteps__title">{t('ion.nextstep.title')}</h4>
      <p class="ion-nextsteps__lead">{t('ion.nextstep.lead')}</p>

      <div class="ion-nextsteps__ctas" role="group">
        {VALIDATION_CTAS.map(({ cta, key }) => {
          const done = requested.has(cta)
          return (
            <button
              key={cta}
              type="button"
              class={`ion-nextsteps__btn${done ? ' ion-nextsteps__btn--done' : ''}`}
              disabled={done || sending === cta}
              onClick={() => fire(cta)}
            >
              {done ? `✓ ${t(key)}` : sending === cta ? `${t(key)}…` : t(key)}
            </button>
          )
        })}
      </div>

      <div class="ion-nextsteps__profile">
        <input
          type="text"
          class="ion-nextsteps__profile-input"
          placeholder={t('ion.nextstep.profile.placeholder')}
          value={message}
          onInput={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && message.trim()) fire('leave_profile', message.trim())
          }}
        />
        <button
          type="button"
          class="ion-nextsteps__btn ion-nextsteps__btn--primary"
          disabled={!message.trim() || sending === 'leave_profile'}
          onClick={() => fire('leave_profile', message.trim())}
        >
          {t('ion.nextstep.profile.send')}
        </button>
      </div>

      {requested.has('leave_profile') && (
        <p class="ion-nextsteps__thanks">{t('ion.nextstep.thanks')}</p>
      )}
      {error && <p class="ion-nextsteps__error">{error}</p>}
    </section>
  )
}
